import { BrowserRouter, Route, Routes } from 'react-router-dom';
import './App.css';
import React, { useEffect, useState } from 'react';
import { UserModel } from './utils/interfaces';
import Loading from './components/Loading';
import { appName, languages, storageKeys, storages, tables } from './utils/constants';
import Home from './pages/Home/Home';
import SignIn from './pages/Login/SignIn';
import SignUp from './pages/Login/SignUp';
import { blobToBase64, useAsyncEffect } from './utils/functions';
import PageNotFound from './pages/PageNotFound';
import Unauthorized from './pages/Unauthorized';
import './i18n';
import { useTranslation } from 'react-i18next';
import { getFileContent, getItem } from './api/API';
import Docs from './pages/Docs/Docs';
import Settings from './pages/Settings/Settings';
import Pricing from './pages/Pricing/Pricing';
import Project from './pages/Project/Project';
import i18n from './i18n';

export interface AppProps {
  user: UserModel | null | undefined
  setUser: React.Dispatch<React.SetStateAction<UserModel | null | undefined>>
  userImage: string
  setUserImage: React.Dispatch<React.SetStateAction<string>>
  isAIOpened: boolean
  setIsAIOpened: React.Dispatch<React.SetStateAction<boolean>>
}

function App() {

  const { t } = useTranslation();
  const [user, setUser] = useState<UserModel | null | undefined>(undefined);
  const [userImage, setUserImage] = React.useState('');
  const [isAIOpened, setIsAIOpened] = useState(false);
  const props = { user, setUser, userImage, setUserImage, isAIOpened, setIsAIOpened };

  // load user image
  useAsyncEffect(async () => {
    if (props.user?.picture) {
      const data = (await getFileContent(storages.userFiles, props.user.picture)).blob;
      if (data) {
        const base64 = await blobToBase64(data);
        setUserImage(base64 as string);
      }
    } else {
      setUserImage('');
    }
  }, [props.user]);

  // detect language
  useEffect(() => {
    const acceptedLanguages = Object.keys(languages);
    let userLang = navigator.language || navigator.languages[0]; 
    const lang = localStorage.getItem(storageKeys.language);
    if (!lang) {
      if (!acceptedLanguages.includes(userLang)) userLang = 'en-US';
      i18n.changeLanguage(userLang);
      localStorage.setItem(storageKeys.language, userLang);
    }
  }, []);

  // set title
  useEffect(() => {
    document.title = `${appName} - ${t('home.title')}`;
  }, [t]);

  // load current user
  useAsyncEffect(async () => {
    const userId = localStorage.getItem(storageKeys.userId);
    const userId2 = localStorage.getItem(storageKeys.userId2);
    if (userId) {
      const data = await getItem(tables.users, userId);
      setUser(userId2 && userId2 === data.item?.id2 ? data.item || null : null);
    } else {
      setUser(null);
    }
  }, []);

  return (
    <div className='app'>
      <BrowserRouter>
        <Routes>
          {user === undefined ? <Route path='*' element={<main className='flex items-center justify-center'><Loading solid size={8} /></main>} />
            : <>
              {/* auth */}
              <Route path='/signin' element={<SignIn {...props} />} />
              <Route path='/signup' element={<SignUp {...props} />} />

              <Route path='/' element={<Home {...props} />} />
              {/* project management */}
              <Route path='/project' element={<Project {...props} />} />
              <Route path='/project/:projectId' element={<Project {...props} />} />
              <Route path='/project/:projectId/:service' element={<Project {...props} />} />
              <Route path='/project/:projectId/:service/:serviceName' element={<Project {...props} />} />
              {/* documentation */}
              <Route path='/docs' element={<Docs {...props} />} />
              {/* settings */}
              <Route path='/settings' element={<Settings {...props} />} />
              <Route path='/settings/:option' element={<Settings {...props} />} />
              {/* pricing */}
              <Route path='/pricing' element={<Pricing {...props} />} />

              {/* exceptions */}
              <Route path='/unauthorized' element={<Unauthorized {...props} />} />
              <Route path='/pagenotfound' element={<PageNotFound {...props} />} />
              <Route path='*' element={<PageNotFound {...props} />} />
            </>}
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;